/**
 * White-label NL user-facing copy (M5.md AC-9, §Design gate). Every
 * brand-bearing decline/degradation string is DERIVED from the theme's
 * `brandName` here — never hardcoded in the route or the NL bar. A white-label
 * swap changes the product name in these messages with zero code edits. Pure +
 * browser-safe so the server route and the client bar share ONE source of
 * truth; the deterministic engine never touches it.
 */

export interface NLCopy {
  /** No model configured for this deployment (graceful degrade). */
  notEnabled: string;
  /** Per-tier daily NL cap hit. */
  dailyLimit: string;
  /** Per-request token/cost ceiling exceeded (AC-9). */
  ceilingExceeded: string;
  /** Model unreachable / transient backend error. */
  modelUnreachable: string;
  /** Client-side network failure. */
  networkError: string;
  /** Too many requests (429). */
  rateLimited: string;
  /** Non-429 request failure. */
  requestFailed: string;
  /** Reassurance sub-copy: manual controls always work. */
  manualStillWorks: string;
  /** Degradation sub-copy: the map is untouched, manual controls work. */
  degradedSub: string;
}

/** Build the NL copy set for a brand. `brandName` comes from `getTheme()`. */
export function nlCopy(brandName: string): NLCopy {
  return {
    notEnabled: `Natural-language mapping isn't enabled in ${brandName} — use the manual style and filter controls.`,
    dailyLimit: `You've hit today's natural-language limit — the manual controls still work. It resets tomorrow.`,
    ceilingExceeded: `That ask was too large for ${brandName} to work through — try a simpler question, or use the manual controls.`,
    modelUnreachable: `Couldn't reach the mapping model just now — the manual controls still work.`,
    networkError: `Couldn't reach the server — your map is untouched. Try again in a moment.`,
    rateLimited: `A few too many asks at once — give it a moment and try again.`,
    requestFailed: `That didn't go through — your map is untouched. Try again in a moment.`,
    manualStillWorks: `The manual style and filter controls do everything, just by hand.`,
    degradedSub: `Your map is untouched, and every manual control still works — style, filter, and cluster from the side panel.`,
  };
}
